import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, type Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth-context';
import CreateTrainingDocModal from '../components/CreateTrainingDocModal';

interface TrainingDoc {
  id: string;
  title: string;
  body: string;
  category: 'getting-started' | 'standards' | 'patterns' | 'examples' | 'reference';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const CATEGORY_LABELS: Record<TrainingDoc['category'], string> = {
  'getting-started': 'Getting started',
  standards: 'QC standards',
  patterns: 'Patterns to spot',
  examples: 'Examples',
  reference: 'Reference'
};

export default function TrainingPage() {
  const { profile } = useAuth();
  const [docs, setDocs] = useState<TrainingDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'training'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as TrainingDoc));
      setLoading(false);
    });
    return unsub;
  }, []);

  const byCategory = (Object.keys(CATEGORY_LABELS) as TrainingDoc['category'][]).map((cat) => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    items: docs.filter((d) => d.category === cat)
  }));

  return (
    <div className="page page--training">
      <div className="page__header">
        <div>
          <h1 className="page__title">Training</h1>
          <p className="page__subtitle">
            Onboarding, QC standards, patterns to watch for, examples.
          </p>
        </div>
        {profile?.role === 'admin' && (
          <button className="btn btn--primary" onClick={() => setCreateOpen(true)}>
            + New training doc
          </button>
        )}
      </div>

      <CreateTrainingDocModal open={createOpen} onClose={() => setCreateOpen(false)} />

      {loading ? (
        <div className="empty">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="empty">
          No training docs yet.{' '}
          {profile?.role === 'admin'
            ? 'Click "+ New training doc" to add the first one.'
            : 'Check back soon.'}
        </div>
      ) : (
        <div className="training">
          {byCategory.map((group) =>
            group.items.length === 0 ? null : (
              <section key={group.category} className="training__section">
                <h2 className="training__heading">{group.label}</h2>
                <ul className="training__list">
                  {group.items.map((doc) => {
                    const preview = doc.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                    return (
                      <li key={doc.id} className="training__card">
                        <Link to={`/training/${doc.id}`} className="training__card-link">
                          <h3 className="training__card-title">{doc.title}</h3>
                          <p className="training__card-body">
                            {preview.slice(0, 220)}{preview.length > 220 ? '…' : ''}
                          </p>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )
          )}
        </div>
      )}
    </div>
  );
}
